import { Space, Flex, Button, Divider, Row, Col, Tooltip, Tag } from "antd"
import { Text, Title } from "ui/Typography"
import styles from "./index.module.css"
import React, { useState } from "react"
import PropTypes from "prop-types"
import moment from "moment"
import { useTranslation } from "react-i18next"
import RefundModal from "./Modals/Refund"
import { BOOKING_REQUEST_TYPES, PAYMENT_STATUS } from "../../utils"
import { colors } from "themes/variables"
import { useDispatch } from "react-redux"
import { getRefundDetails } from "store/bookings"
import { numberToCurrency } from "helpers/string"
import { usePermissions } from "hooks/usePermissions"
import { MODULES, ACTIONS } from "utils/permissions"

const PaymentHistory = ({ paymentHistory, bookingStatus, bookingUUID, remainingBalance, paymentStatus }) => {
  const { t } = useTranslation()
  const [isRefundModalOpen, setIsRefundModalOpen] = useState(false)
  const dispatch = useDispatch()
  const { hasPermission } = usePermissions()

  const canEditBooking = hasPermission(MODULES.BOOKINGS, ACTIONS.EDIT)

  const onOpen = async () => {
    setIsRefundModalOpen(true)
    await dispatch(getRefundDetails(bookingUUID))
  }

  const onClose = () => {
    setIsRefundModalOpen(false)
  }
  const bookingStatusLowerCase = bookingStatus?.toLowerCase()
  const isRefundEnable =
    canEditBooking &&
    (bookingStatusLowerCase === BOOKING_REQUEST_TYPES.CANCELED ||
      bookingStatusLowerCase === BOOKING_REQUEST_TYPES.COMPLETED ||
      bookingStatusLowerCase === BOOKING_REQUEST_TYPES.UPCOMING) &&
    Number(remainingBalance) === 0 &&
    paymentStatus?.toLowerCase() !== PAYMENT_STATUS.REFUNDED

  return (
    <>
      <Space direction="vertical" size={8} className={`${styles.paymentHistoryContainer} w-full p-xl pt-0`}>
        <Divider size="small" />
        <Flex align="center" justify="space-between">
          <Title level={4}>{t("client.client_booking.payment_history")}</Title>
          {isRefundEnable && (
            <Button type="link" className="p-0" onClick={onOpen}>
              {t("client.client_booking.refund")}
            </Button>
          )}
        </Flex>
        <Flex vertical style={{ maxHeight: "180px", overflowY: "auto", scrollbarWidth: "none" }}>
          {paymentHistory?.map((history, idx) => {
            const { type, paid_on, method, last4, amount, crew_names, note } = history || {}
            const crewList = crew_names?.split(",").map((n) => n.trim()).filter(Boolean) || []
            const visibleCrew = crewList.slice(0, 2)
            const hiddenCrew = crewList.slice(2)
            const isRefund = type.toLowerCase() === "refund"
            return (
              <React.Fragment key={paid_on || idx}>
                <Row>
                  <Col xs={14}>
                    <Flex vertical>
                      <Text>
                        {type || ""} ({method || ""}
                        {last4 && ` **** ${last4}`})
                      </Text>
                      <Text color={isRefund ? colors.error : colors.textPrimary}>{amount ? numberToCurrency(amount) : ""}</Text>
                      {crewList.length > 0 && (
                        <Flex align="center" wrap="wrap">
                          {visibleCrew.map((name) => (
                            <Tag key={name} color="processing">{name}</Tag>
                          ))}
                          {hiddenCrew.length > 0 && (
                            <Tooltip title={hiddenCrew.join(", ")}>
                              <Tag color="processing" style={{ cursor: "pointer" }}>+{hiddenCrew.length}</Tag>
                            </Tooltip>
                          )}

                        </Flex>
                      )}
                      {note && (
                        <Tooltip title={note}>
                          <Text color={colors.textSecondary} className={styles.noteText}>
                            Note: {note}
                          </Text>
                        </Tooltip>
                      )}
                    </Flex>
                  </Col>
                  <Col xs={10}>
                    {paid_on && (
                      <Flex vertical>
                        <Text>{isRefund ? t("client.client_booking.refunded_on") : t("client.client_booking.paid_on")}</Text>
                        <Text>{moment(paid_on).format("MM/DD/YYYY")}</Text>
                      </Flex>
                    )}
                  </Col>
                </Row>
                <Divider size="small" />
              </React.Fragment>
            )
          })}
        </Flex>
      </Space>
      {isRefundModalOpen && <RefundModal onOpen={isRefundModalOpen} onClose={onClose} bookingUUID={bookingUUID} />}
    </>
  )
}

PaymentHistory.propTypes = {
  paymentHistory: PropTypes.arrayOf(
    PropTypes.shape({
      amount: PropTypes.number,
      paid_on: PropTypes.string,
      type: PropTypes.string,
      method: PropTypes.string,
      last4: PropTypes.string
    })
  ),
  bookingStatus: PropTypes.string,
  bookingUUID: PropTypes.string,
  remainingBalance: PropTypes.number,
  paymentStatus: PropTypes.string
}

export default PaymentHistory
